import { t } from '@/lib/i18n';
import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PageContainer } from '@ant-design/pro-components';
import { PlusOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { canAccess, useAccess } from '@/features/auth/access';
import { requireCanManageUsers } from '@/features/auth/session/guards';
import { useUsers, useUserDepartmentOptions } from '@/features/user/user-api';
import { UserCreateModal } from '@/features/user/create-modal';
import { UserTable } from '@/features/user/table';
import { UserDepartmentTree } from '@/features/user/department/tree';
import { UserTransferActions } from '@/features/user/transfer/actions';
import type { UserQuery } from '@/features/user/user-types';
import { describeError } from '@/lib/http/http-api';
import { useDocumentTitle } from '@/hooks/use-document-title';

/**
 * User management — admin only.
 *
 * The guard runs in `beforeLoad` and the API enforces the same rule per request. Hiding the
 * menu entry is presentation; this is the boundary; `AuthGuard` in `packages/framework` is
 * the one that actually decides.
 */
export const Route = createFileRoute('/_authenticated/system/users/')({
  beforeLoad: async ({ context, location }) => {
    await requireCanManageUsers(context.queryClient, location.href);
  },
  component: UsersPage,
});

function UsersPage() {
  useDocumentTitle(t('用户管理'));
  const [query, setQuery] = useState<UserQuery>({ page: 1, pageSize: 20 });
  const users = useUsers(query);
  useEffect(() => {
    if (users.error) message.error(describeError(users.error));
  }, [users.error]);
  const departments = useUserDepartmentOptions();
  useEffect(() => {
    if (departments.error) message.error(describeError(departments.error));
  }, [departments.error]);
  const access = useAccess();
  const writable = canAccess(access.data, 'users:write');
  const lastPage = Math.max(
    1,
    Math.ceil((users.data?.total ?? 0) / query.pageSize),
  );
  // A write can remove the last matching row; do not stay on an invalid empty page.
  if (
    users.data &&
    !users.isPlaceholderData &&
    users.data.page === query.page &&
    query.page > lastPage
  )
    setQuery({ ...query, page: lastPage });
  const [createOpen, setCreateOpen] = useState(false);

  // Keep the table mounted while querying, including zero-result searches.
  const createButton = (
    <Button
      key="create"
      type="primary"
      icon={<PlusOutlined />}
      onClick={() => setCreateOpen(true)}
    >
      {t('新建用户')}
    </Button>
  );

  return (
    <PageContainer header={{ title: t('用户管理') }}>
      <div className="flex flex-col items-start gap-4 lg:flex-row">
        <UserDepartmentTree
          departments={departments.data ?? []}
          selectedId={query.departmentId}
          onSelect={(departmentId) =>
            setQuery((current) => ({ ...current, departmentId, page: 1 }))
          }
          loading={departments.isFetching}
          error={departments.isError}
          onReload={() => void departments.refetch()}
        />
        <div className="w-full min-w-0 flex-1">
          <UserTable
            users={users.data?.rows ?? []}
            total={users.data?.total ?? 0}
            query={query}
            onQueryChange={setQuery}
            writable={writable}
            isAdministrator={access.data?.isAdmin === true}
            loading={users.isFetching}
            onReload={() => {
              void users.refetch();
              void departments.refetch();
            }}
            toolBar={
              <div className="flex flex-wrap gap-2">
                {canAccess(access.data, 'users:read') ? (
                  <UserTransferActions query={query} writable={writable} />
                ) : null}
                {writable ? createButton : null}
              </div>
            }
          />
        </div>
      </div>

      {createOpen && writable ? (
        <UserCreateModal onClose={() => setCreateOpen(false)} />
      ) : null}
    </PageContainer>
  );
}
